import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  IconButton,
} from "@mui/material";
import { Collect } from "../../../../models/collect";
import { Expense } from "../../../../models/expense";
import { Info as InfoIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

interface CollectDetailsProps {
  open: boolean;
  handleClose: () => void;
  collectsOffice: Collect[];
  collectCheckbox: { [key: number]: boolean };
  handleCheckboxChange: (collectId: number) => void;
  collect: Collect | null;
  expenses: Expense[];
  setValue: (value: number, expenseId: number) => void;
  isEditable: boolean;
}

const CollectDetails: React.FC<CollectDetailsProps> = ({
  open,
  handleClose,
  collectsOffice,
  collectCheckbox,
  handleCheckboxChange,
  collect,
  expenses,
  setValue,
  isEditable,
}) => {
  const [selectedExpenses, setSelectedExpenses] = useState<number[]>([]);
  const [tempValues, setTempValues] = useState<Record<number, string>>({});

  const applyTempValues = () => {
    Object.entries(tempValues).forEach(([expenseId, rawValue]) => {
      const value = rawValue.trim();

      if (!value) {
        setValue(0, Number(expenseId));
        return;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue) || numericValue <= 0) {
        setValue(0, Number(expenseId));
        return;
      }

      setValue(numericValue, Number(expenseId));
    });
  };

  const handleModalClose = () => {
    applyTempValues();
    handleClose();
  };

  const handleBlur = (expenseId: number, event: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.trim();

    if (!rawValue) {
      setTempValues((prev) => ({ ...prev, [expenseId]: "" }));
      setValue(0, expenseId);
      return;
    }

    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      setTempValues((prev) => ({ ...prev, [expenseId]: "" }));
      setValue(0, expenseId);
      return;
    }

    setValue(numericValue, expenseId);
    setTempValues((prev) => ({ ...prev, [expenseId]: rawValue }));
  };

  const handleInputChange = (expenseId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTempValues((prev) => ({
      ...prev,
      [expenseId]: value,
    }));
  };

  const handleExpenseSelection = (expenseId: number) => {
    setSelectedExpenses((prev) => {
      const isSelected = prev.includes(expenseId);

      if (isSelected) {
        setValue(0, expenseId);
        return prev.filter((id) => id !== expenseId);
      } else {
        return [...prev, expenseId];
      }
    });
  };

  useEffect(() => {
    if (!open) {
      setSelectedExpenses([]);
      return;
    }

    const selectedIds = expenses
      .filter((expense) => Number(expense.value || 0) > 0)
      .map((expense) => expense.id);

    setSelectedExpenses(selectedIds);
  }, [open, expenses]);

  useEffect(() => {
    if (!open) {
      setTempValues({});
      return;
    }

    setTempValues((prev) => {
      const updated: Record<number, string> = {};

      selectedExpenses.forEach((id) => {
        if (prev[id] !== undefined) {
          updated[id] = prev[id];
          return;
        }

        const expense = expenses.find((exp) => exp.id === id);
        if (expense && expense.value !== undefined && expense.value !== null && expense.value !== 0) {
          updated[id] = expense.value.toString();
        } else {
          updated[id] = "";
        }
      });

      const prevKeys = Object.keys(prev);
      const hasChanges =
        prevKeys.length !== selectedExpenses.length ||
        selectedExpenses.some((id) => updated[id] !== prev[id]);

      return hasChanges ? updated : prev;
    });
  }, [open, selectedExpenses, expenses]);

  const getDisplayValue = (expense: Expense) => {
    if (tempValues[expense.id] !== undefined) {
      return tempValues[expense.id];
    }

    if (expense.value !== undefined && expense.value !== null && expense.value !== 0) {
      return expense.value.toString();
    }

    return "";
  };

  const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    maxWidth: 500,
    height: "80vh",
    maxHeight: 500,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 2,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography id="modal-title" variant="h6" component="h2">
            Ingresar Detalle de Gastos
          </Typography>
          <IconButton onClick={handleModalClose} sx={{ color: "white" }} aria-label="cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Cobro seleccionado
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {collect?.name || "Sin cobro seleccionado"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Seleccionar Gastos
          </Typography>
          <FormGroup>
            <Grid container >
              {expenses.map((expense) => (
                <Grid item xs={6} key={expense.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedExpenses.includes(expense.id)}
                        onChange={() => handleExpenseSelection(expense.id)}
                        disabled={!isEditable}
                      />
                    }
                    label={expense.name}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {selectedExpenses.length > 0 ? (
              selectedExpenses.map((expenseId) => {
                const expense = expenses.find((exp) => exp.id === expenseId);
                if (!expense) return null;

                return (
                  <Grid item xs={12} key={expense.id} container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <Typography variant="body2">{expense.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Valor"
                        placeholder="1000"
                        type="number"
                        required
                        size="small"
                        value={getDisplayValue(expense)}
                        onChange={(event) => handleInputChange(expense.id, event as React.ChangeEvent<HTMLInputElement>)}
                        onBlur={(event) => handleBlur(expense.id, event as React.FocusEvent<HTMLInputElement>)}
                        inputProps={{ maxLength: 20, min: 0 }}
                        sx={{
                          "& .MuiInputBase-input": {
                            fontSize: "0.875rem",
                            padding: "8px",
                          },
                        }}
                        disabled={!isEditable || !selectedExpenses.includes(expense.id)}
                      />
                    </Grid>
                  </Grid>
                );
              })
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" py={3}>
                <InfoIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="body1">No hay detalles de operación seleccionados</Typography>
              </Box>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button variant="contained" onClick={handleModalClose}>
            Cerrar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CollectDetails;
